import { Card } from '../Card'
import Styles from './ReviewRequestCard.module.scss'
import {
  InformalFeedbackRequest,
  InformalFeedbackReviewDismissReason,
  InformalFeedbackReviewState,
} from 'api/clients/InformalFeedbackRequestsClient'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Header } from './Header'
import { ReviewersList } from './ReviewersList'
import { InformalFeedbackRequestsService } from 'api/services/AlmanacFiles/InformalFeedbackRequestsService'
import { useQueryClient } from 'react-query'
import { useStore } from 'react-redux'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { reportError } from 'utils/error'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { Content } from './Content'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { ViewSummaryButton } from './Buttons/ViewSummaryButton'
import cx from 'classnames'

interface Props {
  request: InformalFeedbackRequest
  onDismiss: (value: boolean) => void
}

export const RequestEndedCard = ({ request, onDismiss }: Props) => {
  const queryClient = useQueryClient()
  const store = useStore()

  if (!request) return null

  const reviews = request.reviews.filter(
    (review) => review.dismissed_reason !== InformalFeedbackReviewDismissReason.RemovedFromReview,
  )
  const submittedReviews = reviews.filter(
    (review) =>
      review.state === InformalFeedbackReviewState.Submitted ||
      review.dismissed_reason === InformalFeedbackReviewDismissReason.RequestedAnotherReview,
  )

  const handleDismiss = async () => {
    onDismiss(true)
    const res = await InformalFeedbackRequestsService.update(
      store,
      request.id,
      request.almanac_file.id,
      { cleanupDismissed: true },
      queryClient,
    )

    if (res.type !== SUCCESS_RESPONSE_TYPE) {
      onDismiss(false)
      showErrorMessageToast('An error occurred! Our team has been notified and we are looking into it.')
      reportError('Failed to load review request from server', {
        severity: 'error',
        metaData: { request },
      })
    }
  }

  return (
    <Card className={cx(Styles.reviewRequestCard, { [Styles.isDesign]: !!request.design })}>
      <Header status="Ended" background="rgba(255, 203, 203, 1)" color="rgba(124, 0, 0, 1)" onPress={handleDismiss} />
      <Content request={request}>
        <Heading level={5}>
          {submittedReviews.length} out of {reviews.length} people reviewed
        </Heading>
        <ResponsiveDesign.Desktop>
          <ReviewersList request={request} />
        </ResponsiveDesign.Desktop>
      </Content>
      <div className={Styles.footer}>
        <ViewSummaryButton request={request} fullWidth variant="tertiary" />
      </div>
    </Card>
  )
}
